'use strict';
import 'rxjs/add/observable/timer';
import 'rxjs/add/operator/mapTo';
import 'rxjs/add/operator/toPromise';
import {Observable} from 'rxjs/Observable';
import {getIsAuthenticated} from '../../auth/reducers/state';
import {isCordovaBuild} from '../../config/constants';
import {getIsOnline} from '../../settings/reducers/state';
import {$settingsProvider} from '../../settings/services/AppSettingsService';
import {AudioNotification} from '../enums/AudioNotification';
import {AudioNotificationSounds} from './audioNotifications';


/**
 * References:
 * https://developer.mozilla.org/en-US/docs/Web/Apps/Fundamentals/Audio_and_video_delivery/Cross-browser_audio_basics
 * https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement
 * https://www.w3.org/TR/html5/embedded-content-0.html#concept-media-load-algorithm
 */
export class AudioNotificationService {
  static ID = 'AudioNotificationService';
  static SERVICE_NAME = 'audioNotificationService';
  static DURATION = 500;

  static $inject = [
    $settingsProvider.SERVICE_NAME,
    '$ngRedux',
  ];

  static normalSettingsVolume(volume) {
    return volume / 100;
  }

  static getId(type) {
    return `${AudioNotificationService.ID}_${type}`;
  }

  /**
   * @param {HTMLAudioElement} elem
   * @param {Number} volume must be between 0 and 1
   * @private
   */
  static changeVolumeForEl(elem, volume) {
    elem.volume = volume;
  }

  constructor(
    $settings,
    $ngRedux,
  ) {
    this.$settings = $settings;
    this.$ngRedux = $ngRedux;

    this.audioElement = {};
    this.errorHandlers = {};

    this.volumeSubscription = $settings.audioN10nVolume$
      .debounceTime(100)
      // Convert the setting volume value to the 0-1 range used by the audio element.
      .subscribe(volume => this.changeVolume(AudioNotificationService.normalSettingsVolume(volume)));
  }

  destroy() {
    this.audioElement = null;
    this.volumeSubscription && this.volumeSubscription.unsubscribe();
  }

  supportsAudio() {
    return !!this.audioElement;
  }

  canLoadAudio() {
    const rootState = this.$ngRedux.getState();
    return isCordovaBuild() || getIsOnline(rootState) || !getIsAuthenticated(rootState);
  }

  /**
   * Create an audio element.
   * @param type
   * @returns {*}
   * @private
   */
  initSound(type) {
    const id = AudioNotificationService.getId(type);
    if (this.audioElement === null || !this.canLoadAudio()) {
      return null;
    } else if (this.audioElement[id]) {
      return this.audioElement[id];
    }

    try {
      const elem = document.createElement('audio');
      elem.id = id;
      elem.style = 'height: 0; width: 0';

      if (elem.canPlayType('audio/ogg')) {
        elem.setAttribute('src', AudioNotificationSounds[type].ogg);
      } else if (elem.canPlayType('audio/mpeg')) {
        elem.setAttribute('src', AudioNotificationSounds[type].mp3);
      } else {
        // Missing supported sound types.
        this.destroy();
        return;
      }

      try {
        // Not clear from MDN whether volume is supported on all browser. try...catch to be safe.
        const volume = AudioNotificationService.normalSettingsVolume(this.$settings.audioN10nVolume);
        AudioNotificationService.changeVolumeForEl(elem, volume);
      } catch (e) {
        // console.warn('browser does not support audio volume property');
      }

      // memoize the error handler. We only need to make this once, even if we need to remove and recreate the element.
      if (!this.errorHandlers[id]) {
        this.errorHandlers[id] = e => this.handleError(id, e);
      }
      elem.addEventListener('error', this.errorHandlers[id]);

      document.body.appendChild(elem);

      this.audioElement[id] = elem;
      return elem;
    } catch (e) {
      this.destroy();
      // console.warn('browser does not support audio');
      return null;
    }
  }

  /**
   * @param id
   * @param e error event
   * @private
   */
  handleError(id, e) {
    // For now, just remove the element and try again on the next sound play.
    // TODO: need to track there are any other errors and see if there are errors other than network that we need to respond to.
    this.destroySound(id);
  }

  /**
   * Remove an audio element from the DOM.
   * Remove the cached reference to the element from the service.
   * @param id
   * @private
   */
  destroySound(id) {
    // Cleanup the service reference
    const elem = this.audioElement[id];
    delete this.audioElement[id];

    // Cleanup the DOM
    elem.removeEventListener('error', this.errorHandlers[id]);
    while (elem.hasChildNodes()) {
      elem.removeChild(elem.firstChild);
    }
    elem.parentNode.removeChild(elem);
  }

  /**
   * Update all audio elements to the specified volume.
   * @param {Number} volume must be between 0 and 1
   * @private
   */
  changeVolume(volume) {
    if (this.audioElement) {
      try {
        // Not clear from MDN whether volume is supported on all browser. try...catch to be safe.
        Object.keys(this.audioElement)
          .forEach(id => AudioNotificationService.changeVolumeForEl(this.audioElement[id], volume));
      } catch (e) {
        console.warn('browser does not support audio volume property');
      }
    }
  }

  /**
   * Plays the specified type according to the current app settings.
   * @param {HTMLAudioElement} elem
   * @returns {Promise}
   * @private
   */
  playAudio(elem) {
    if (!this.supportsAudio()) {
      return Promise.reject(new Error('browser does not support audio'));
    } else if (!this.$settings.useAudioN10ns) {
      return Promise.resolve(null);
    }

    try {
      // Older browsers may not return a promise here.
      return (elem.play() || Promise.resolve())
        .then(result => Observable
          .timer(AudioNotificationService.DURATION)
          .mapTo(result)
          .toPromise()
        );
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Initializes the type, then plays the audio file associated with the specified type.
   * @param {AudioNotification} type
   * @returns {Promise}
   */
  playFor(type) {
    switch (type) {
      case AudioNotification.login:
      case AudioNotification.logout:
      case AudioNotification.message:
      case AudioNotification.negative:
      case AudioNotification.positive:
        const elem = this.initSound(type);
        if (elem) {
          return this.playAudio(elem);
        } else {
          return Promise.reject(new Error('browser does not support audio'));
        }
      default:
        return Promise.reject(new Error(`unsupported type: '${type}'`));
    }
  }

  playLogin() {
    return this.playFor(AudioNotification.login);
  }

  playLogout() {
    return this.playFor(AudioNotification.logout);
  }

  playMessage() {
    return this.playFor(AudioNotification.message);
  }

  playNegative() {
    return this.playFor(AudioNotification.negative);
  }

  playPositive() {
    return this.playFor(AudioNotification.positive);
  }
}



// WEBPACK FOOTER //
// ./src/js/core-utilities/notifications/AudioNotificationService.js